import React, { useState, useEffect } from 'react';
import { useApi } from '../../apiHelpers';
import { parseDate, formatEventDate, businessToday } from '../../utils/date-utils';
import Panel from '../../components/ui/Panel';
import { ListStack, ListRow } from '../../components/ui/ListRow';
import { CATEGORY } from '../../components/ui/categoryColors';

const zones = [
  { name: 'RED', min: 0, max: 2, hue: 'red' },
  { name: 'ORANGE', min: 3, max: 4, hue: 'orange' },
  { name: 'YELLOW', min: 5, max: 6, hue: 'yellow' },
];

/**
 * Which shipping zone an event falls in, and how many whole days away it is.
 * Returns null for an event outside every zone (or with no usable date).
 *
 * BOTH OPERANDS MUST BE MIDNIGHT. This used to be
 * `dayjs(e.event_date).diff(dayjs(), 'day')` — a midnight diffed against a
 * time-of-day. `.diff(_, 'day')` truncates the partial day, so it reported N-1
 * for an event N days out at every hour past midnight, and the zone boundaries
 * are exactly where that lands wrong: a show 3 days out painted RED instead of
 * ORANGE, one 5 days out ORANGE instead of YELLOW, and one 7 days out shown at
 * all when it belongs in no zone.
 *
 * parseDate() is UTC-midnight and businessToday() is UTC-midnight on the Eastern
 * calendar date, so the subtraction is whole days by construction. Pass `today`
 * in rather than calling businessToday() here: one clock for a whole list, and
 * the tests get to pin it.
 *
 * Exported for the tests — the component renders nothing until a fetch resolves,
 * so the bucketing is only reachable as a pure function.
 */
export const zoneFor = (eventDate, today) => {
  const daysUntil = parseDate(eventDate)?.diff(today, 'day');
  if (daysUntil == null || Number.isNaN(daysUntil)) return null;
  const zone = zones.find(z => daysUntil >= z.min && daysUntil <= z.max);
  return zone ? { daysUntil, zone } : null;
};

const ShippingZoneAlertsModule = () => {
  const api = useApi();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await api.get('/events', { silent: true });
        const today = businessToday();
        const urgent = (data || [])
          .filter(e => ['confirmed', 'packing'].includes(e.status))
          .map(e => {
            const hit = zoneFor(e.event_date, today);
            return hit ? { ...e, ...hit } : null;
          })
          .filter(Boolean)
          .sort((a, b) => a.daysUntil - b.daysUntil);
        setAlerts(urgent);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return null;
  if (alerts.length === 0) return null;

  return (
    <Panel
      title="Shipping Alerts"
      titleStyle="section"
      accent="red"
      lit
      level={3}
      actions={<span className="w-2 h-2 rounded-full animate-pulse" style={{ background: CATEGORY.red.mark.oklch }} />}
    >
      <ListStack
        label="Shipping alerts"
        on="panel"
        items={alerts}
        getKey={(event) => String(event.id)}
        renderRow={(event) => {
          const totalDrones = (parseInt(event.ls_drone_count) || 0) + (parseInt(event.pyro_drone_count) || 0);
          return (
            <ListRow
              to={`/events/${event.id}`}
              leading={<span className="h-2 w-2 rounded-full" style={{ background: CATEGORY[event.zone.hue].mark.oklch }} />}
              title={event.name}
              meta={`${formatEventDate(event.event_date, 'MMM D')}, ${totalDrones} drones`}
              trailing={(
                <span className="text-xs font-bold tabular-nums" style={{ color: CATEGORY[event.zone.hue].ink.oklch }}>
                  {event.daysUntil === 0 ? 'TODAY' : `${event.daysUntil}d`}
                </span>
              )}
            />
          );
        }}
      />
    </Panel>
  );
};

export default ShippingZoneAlertsModule;
